import { useEffect, useRef } from "react";

type SafeImgProps = React.ImgHTMLAttributes<HTMLImageElement> & {
  onFail?: () => void;
};

/**
 * <img> that reliably reports load failure even on the SSR build, where the
 * browser may fire the native `error` event before React hydrates and attaches
 * its synthetic onError. Attaches a native error listener and also checks
 * `complete && naturalWidth === 0` on mount to catch already-failed images.
 */
export function SafeImg({ onFail, ...props }: SafeImgProps) {
  const ref = useRef<HTMLImageElement | null>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el || !onFail) return;
    if (el.complete && el.naturalWidth === 0) {
      onFail();
      return;
    }
    const handler = () => onFail();
    el.addEventListener("error", handler);
    return () => el.removeEventListener("error", handler);
  }, [props.src, onFail]);
  return <img ref={ref} {...props} />;
}
